import React, { Component } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';
import { injectIntl, FormattedMessage } from 'react-intl';

import s from './OnlineProviders.css';
import messages from '../../../locale/messages';
import { api, profilePhotouploadDir } from '../../../config';

const avatarUrl = (picture) => (picture ? `${api.apiEndpoint}${profilePhotouploadDir}medium_${picture}` : null);

// NOTE: UI slicing stage — static dummy data. Will be replaced by a live
// socket feed (online/offline) + query (location) in the API stage.
const DUMMY_PROVIDERS = [
	{ id: '1', name: 'Ahmed Khan', picture: null, status: 'online', location: 'Dubai Marina' },
	{ id: '2', name: 'Sara Mansour', picture: null, status: 'online', location: 'Deira, Dubai' },
	{ id: '3', name: 'John Doe', picture: null, status: 'online', location: '25.2700, 55.3100' },
	{ id: '4', name: 'Omar Tariq', picture: null, status: 'offline', location: 'Jebel Ali' },
	{ id: '5', name: 'Lena Park', picture: null, status: 'online', location: 'Business Bay' },
];

const statusDotClass = {
	online: s.dotAvailable,
	offline: s.dotOffline,
};

const statusLabel = {
	online: messages.online,
	offline: messages.offline,
};

class OnlineProviders extends Component {
	static propTypes = {
		providers: PropTypes.array,
		summary: PropTypes.shape({
			online: PropTypes.number,
			offline: PropTypes.number,
		}),
		onRefresh: PropTypes.func,
	};

	static defaultProps = {
		providers: DUMMY_PROVIDERS,
		summary: { online: 12, offline: 30 },
		onRefresh: () => {},
	};

	constructor(props) {
		super(props);
		this.state = {
			limit: 10,
			search: '',
			statusFilter: 'online',
			serviceFilter: 'all',
		};
	}

	render() {
		const { providers, summary, onRefresh } = this.props;
		const { limit, search, statusFilter, serviceFilter } = this.state;
		const normalizedSearch = search.trim().toLowerCase();
		const services = providers
			.map((provider) => provider.activeService)
			.filter(Boolean)
			.filter((service, index, list) => list.indexOf(service) === index)
			.sort((a, b) => a.localeCompare(b));
		const filteredProviders = providers.filter((provider) => {
			const matchesSearch = !normalizedSearch || [provider.name, provider.location, provider.activeService]
				.filter(Boolean)
				.some((value) => value.toLowerCase().indexOf(normalizedSearch) >= 0);
			const matchesStatus = statusFilter === 'all' || provider.status === statusFilter;
			const matchesService = serviceFilter === 'all' || provider.activeService === serviceFilter;
			return matchesSearch && matchesStatus && matchesService;
		});
		const rows = filteredProviders.slice(0, limit);

		const chips = [
			{ key: 'online', value: summary.online, label: messages.online },
			{ key: 'offline', value: summary.offline, label: messages.offline },
		];

		return (
			<div className={cx(s.section, 'textWhite')}>
				<div className={s.headerRow}>
					<h2 className={s.heading}><FormattedMessage {...messages.onlineProviders} /></h2>
					<button type="button" className={s.refreshBtn} onClick={onRefresh}>
						<FormattedMessage {...messages.refresh} />
					</button>
				</div>

				<div className={s.chips}>
					{chips.map((chip) => (
						<div key={chip.key} className={cx(s.chip, 'bgBlackTwo')}>
							<span className={s.chipValue}>{chip.value}</span>
							<span className={s.chipLabel}><FormattedMessage {...chip.label} /></span>
						</div>
					))}
				</div>

				<div className={s.filters} aria-label="Provider filters">
					<label className={s.filterField}>
						<span>Search</span>
						<input
							type="search"
							value={search}
							placeholder="Provider, location, or service"
							onChange={(e) => this.setState({ search: e.target.value, limit: 10 })}
						/>
					</label>
					<label className={s.filterField}>
						<span>Status</span>
						<select
							value={statusFilter}
							onChange={(e) => this.setState({ statusFilter: e.target.value, limit: 10 })}
						>
							<option value="all">All statuses</option>
							<option value="online">Online</option>
							<option value="offline">Offline</option>
						</select>
					</label>
					<label className={s.filterField}>
						<span>Active service</span>
						<select
							value={serviceFilter}
							onChange={(e) => this.setState({ serviceFilter: e.target.value, limit: 10 })}
						>
							<option value="all">All services</option>
							{services.map((service) => <option key={service} value={service}>{service}</option>)}
						</select>
					</label>
					<div className={s.resultCount} aria-live="polite">
						{filteredProviders.length} provider{filteredProviders.length === 1 ? '' : 's'}
					</div>
				</div>

				<div className={cx(s.tableWrap, 'bgBlackTwo')}>
					<table className={s.table}>
						<thead>
							<tr>
								<th><FormattedMessage {...messages.provider} /></th>
								<th><FormattedMessage {...messages.status} /></th>
								<th>Active service</th>
								<th><FormattedMessage {...messages.locationColumn} /></th>
							</tr>
						</thead>
						<tbody>
							{rows.length === 0 && (
								<tr>
									<td colSpan={4} className={s.empty}>
										No providers match these filters.
									</td>
								</tr>
							)}
							{rows.map((p) => (
								<tr key={p.id}>
									<td>
										<div className={s.providerCell}>
											{p.picture
												? <img className={s.avatar} src={avatarUrl(p.picture)} alt={p.name} />
												: <span className={s.avatar} />}
											<span>{p.name}</span>
										</div>
									</td>
									<td>
										<div className={s.statusCell}>
											<span className={cx(s.dot, statusDotClass[p.status])} />
											<FormattedMessage {...(statusLabel[p.status] || messages.offline)} />
										</div>
									</td>
									<td>{p.activeService || <span className={s.muted}>—</span>}</td>
									<td>{p.location || <span className={s.muted}>-</span>}</td>
								</tr>
							))}
						</tbody>
					</table>

					<div className={s.footerRow}>
						<select
							className={s.footerSelect}
							value={limit}
							onChange={(e) => this.setState({ limit: Number(e.target.value) })}
						>
							<option value={10}>10</option>
							<option value={50}>50</option>
							<option value={100}>100</option>
						</select>
					</div>
				</div>
			</div>
		);
	}
}

export default injectIntl(withStyles(s)(OnlineProviders));
